import { Check } from '@mui/icons-material';
import { Box, ListItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
    SEARCH_VERSION_PREFERENCE_OPTIONS,
    SearchVersionPreference,
} from 'shared/const/searchVersionPreference';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';

import {
    getLastUsedSearchVersion,
    getSearchVersionPreference,
} from '../../model/selectors/searchVersionPreferenceSelectors';
import { setSearchVersionPreference } from '../../model/services/setSearchVersionPreference/setSearchVersionPreference';

const listItemStyles = {
    sx: {
        padding: 0,
        ':hover': {
            bgcolor: 'action.hover',
            cursor: 'pointer',
        },
    },
};

const preferenceLabelKeys: Record<SearchVersionPreference, string> = {
    auto: 'Search version auto',
    v1: 'Search version v1',
    v2: 'Search version v2',
};

export const SearchVersionPreferenceMenuItems = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const preference = useSelector(getSearchVersionPreference);
    const lastUsedVersion = useSelector(getLastUsedSearchVersion);

    const onSelectPreference = (
        event: React.MouseEvent,
        nextPreference: SearchVersionPreference,
    ) => {
        event.stopPropagation();

        if (nextPreference === preference) {
            return;
        }

        dispatch(setSearchVersionPreference(nextPreference));
    };

    return (
        <Box
            onClick={(event) => event.stopPropagation()}
            sx={{
                borderTop: '1px solid',
                borderBottom: '1px solid',
                borderColor: 'divider',
                paddingTop: '8px',
                paddingBottom: '4px',
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    padding: '0 16px 2px',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                }}
            >
                {t('Search version preference')}
            </Typography>

            {SEARCH_VERSION_PREFERENCE_OPTIONS.map((option) => (
                <ListItem
                    key={option}
                    onClick={(event) => onSelectPreference(event, option)}
                    {...listItemStyles}
                    data-testclass={`search-version-preference-${option}`}
                >
                    <Box
                        sx={{
                            width: '100%',
                            minHeight: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 16px 0 12px',
                        }}
                    >
                        <Check
                            sx={{
                                width: '16px',
                                height: '16px',
                                visibility:
                                    preference === option
                                        ? 'visible'
                                        : 'hidden',
                                color: 'primary.main',
                            }}
                        />
                        <Typography
                            variant="body1"
                            sx={{
                                flex: 1,
                                marginLeft: '4px',
                                color: 'text.secondary',
                            }}
                        >
                            {t(preferenceLabelKeys[option])}
                        </Typography>
                    </Box>
                </ListItem>
            ))}

            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    padding: '2px 16px 4px',
                    color: 'text.disabled',
                }}
                data-testclass="search-version-last-used"
            >
                {t('Search version last request', {
                    version: lastUsedVersion
                        ? lastUsedVersion.toUpperCase()
                        : t('Search version last request unknown'),
                })}
            </Typography>
        </Box>
    );
};
